import { useState, useRef, useEffect, useCallback } from "react";
import api, { presignedUpload } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { ScrollArea } from "../components/ui/scroll-area";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "../components/ui/tooltip";
import { Progress } from "../components/ui/progress";
import { Skeleton } from "../components/ui/skeleton";
import {
  DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors
} from "@dnd-kit/core";
import {
  arrayMove, SortableContext, sortableKeyboardCoordinates,
  useSortable, verticalListSortingStrategy
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  Send, Sparkles, Play, RefreshCw, Check,
  ImageIcon, Clock, Film, Loader2, Copy,
  CheckCircle2, AlertCircle, Zap, User, Video, Eye,
  GripVertical, Pencil, X, Maximize2, Volume2, Save, ArrowLeftRight
} from "lucide-react";
import { PhotoPickerDialog } from "../components/storyboard/PhotoPickerDialog";
import { toast } from "sonner";

function getShots(session) {
  const sb = session?.storyboard;
  if (!sb) return [];
  return sb.shot_list || sb.shots || [];
}

function getShotField(shot, field) {
  const map = {
    room: shot.room_name || shot.room_label,
    voiceover: shot.voiceover_segment || shot.voiceover_text,
    imageIdx: shot.related_image_index ?? shot.image_index ?? 0
  };
  return map[field];
}

function getImageUrl(img) { return typeof img === 'string' ? img : img?.url || ''; }
function getThumbUrl(img) { return typeof img === 'string' ? img : (img?.thumbnail_url || img?.url || ''); }

// Sortable shot card wrapper
function SortableShotCard({ shot, idx, children }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: shot.id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 50 : 'auto',
  };
  return (
    <div ref={setNodeRef} style={style} {...attributes}>
      {children({ listeners, isDragging })}
    </div>
  );
}

export default function StoryboardView({ session, onUpdate, hideRenderButton = false }) {
  const [chatInput, setChatInput] = useState("");
  const [chatMessages, setChatMessages] = useState([]);
  const [chatLoading, setChatLoading] = useState(false);
  const [generatingShot, setGeneratingShot] = useState(null);
  const [generatingAll, setGeneratingAll] = useState(false);
  const [showRenderDialog, setShowRenderDialog] = useState(false);
  const [rendering, setRendering] = useState(false);
  const [expandedShot, setExpandedShot] = useState(null);
  const [editingShot, setEditingShot] = useState(null);
  const [editText, setEditText] = useState("");
  const [editRoom, setEditRoom] = useState("");
  const [lightboxImage, setLightboxImage] = useState(null);
  
  const [hasReordered, setHasReordered] = useState(false);
  const [regeneratingScript, setRegeneratingScript] = useState(false);
  const [editingAvatar, setEditingAvatar] = useState(null); // 'intro' | 'cta' | null
  const [editAvatarScript, setEditAvatarScript] = useState("");
  const [videoPlayer, setVideoPlayer] = useState(null); // { url, title }
  const [promptingShot, setPromptingShot] = useState(null); // shot id for custom prompt input
  const [clipPromptText, setClipPromptText] = useState("");
  const [photoPickerShot, setPhotoPickerShot] = useState(null); // shot id for photo picker
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [photoChangedShots, setPhotoChangedShots] = useState({}); // { shotId: true }
  const chatEndRef = useRef(null);
  const shots = getShots(session);
  const storyboard = session?.storyboard || {};
  const images = session?.property_image_urls || [];

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [chatMessages]);

  // Auto-persist clips with temporary URLs to S3
  useEffect(() => {
    const s3Bucket = process.env.REACT_APP_S3_BUCKET || '';
    const hasNonS3Clips = shots.some(shot =>
      (shot.candidates || []).some(c =>
        c.video_url && c.status === 'Ready' && !c.video_url.includes('.s3.') && !c.video_url.includes('s3.amazonaws.com')
      )
    );
    if (hasNonS3Clips && session?.id) {
      api.post(`/sessions/${session.id}/persist-clips`).then(res => {
        if (res.data.migrated > 0) {
          toast.success(`${res.data.migrated} clip(s) saved successfully`);
          api.get(`/sessions/${session.id}`).then(r => onUpdate(r.data)).catch(() => {});
        }
      }).catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session?.id]);

  const selectedCount = shots.filter(s => s.selected_candidate_id).length;
  const allReady = shots.length > 0 && selectedCount === shots.length;
  const progressPct = shots.length > 0 ? (selectedCount / shots.length) * 100 : 0;

  const handleChat = async () => {
    if (!chatInput.trim()) return;
    const msg = chatInput;
    setChatMessages(prev => [...prev, { role: "user", text: msg }]);
    setChatInput("");
    setChatLoading(true);
    try {
      await api.post(`/sessions/${session.id}/chat`, { message: msg });
      // Poll for completion
      const poll = setInterval(async () => {
        try {
          const res = await api.get(`/sessions/${session.id}/chat-status`);
          const { status, summary, error, session: updatedSession } = res.data;
          if (status === 'done') {
            clearInterval(poll);
            setChatLoading(false);
            onUpdate(updatedSession);
            setChatMessages(prev => [...prev, { role: "ai", text: summary }]);
            setHasReordered(false);
          } else if (status === 'error') {
            clearInterval(poll);
            setChatLoading(false);
            console.error("Chat edit error:", error);
            setChatMessages(prev => [...prev, { role: "ai", text: `I couldn't make that change: ${error}` }]);
            toast.error("The edit didn't go through. Please try again.");
          }
        } catch (pollErr) {
          console.error("Poll error:", pollErr);
        }
      }, 3000);
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message || "Unknown error";
      console.error("Chat edit start error:", err?.response?.status, detail);
      setChatMessages(prev => [...prev, { role: "ai", text: `I couldn't make that change: ${detail}` }]);
      toast.error("The edit didn't go through. Please try again.");
      setChatLoading(false);
    }
  };

  const handleGenerateShot = async (shotId, customPrompt = null) => {
    setGeneratingShot(shotId);
    try {
      await api.post(`/sessions/${session.id}/shots/${shotId}/generate`, { custom_prompt: customPrompt || null });
      toast.success("Creating your video clip...");
      // Poll for completion
      const poll = setInterval(async () => {
        try {
          const res = await api.get(`/sessions/${session.id}`);
          onUpdate(res.data);
          const shots = res.data?.storyboard?.shot_list || res.data?.storyboard?.shots || [];
          const shot = shots.find(s => s.id === shotId);
          if (shot && shot.status !== 'Generating') {
            clearInterval(poll);
            setGeneratingShot(null);
            if (shot.status === 'Failed') toast.error("Something went wrong creating this clip. Please try again.");
          }
        } catch {}
      }, 4000);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Something went wrong. Please try again.");
      setGeneratingShot(null);
    }
  };

  const handleGenerateAll = async () => {
    setGeneratingAll(true);
    try {
      const res = await api.post(`/sessions/${session.id}/generate-all-clips`);
      toast.success(`${res.data.clips_queued} video clips are being created`);
      // Poll to update shot statuses in the UI
      const poll = setInterval(async () => {
        try {
          const sess = await api.get(`/sessions/${session.id}`);
          onUpdate(sess.data);
          const shots = sess.data?.storyboard?.shot_list || sess.data?.storyboard?.shots || [];
          const stillGenerating = shots.some(s => s.status === 'Generating');
          if (!stillGenerating) {
            clearInterval(poll);
            setGeneratingAll(false);
            toast.success("All video clips are ready!");
          }
        } catch {}
      }, 5000);
      // Safety timeout
      setTimeout(() => { clearInterval(poll); setGeneratingAll(false); }, 600000);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Something went wrong. Please try again.");
      setGeneratingAll(false);
    }
  };

  const handleSelectCandidate = async (shotId, candidateId) => {
    try {
      const res = await api.post(`/sessions/${session.id}/shots/${shotId}/select-candidate`, { candidate_id: candidateId });
      onUpdate(res.data);
    } catch { toast.error("Could not select this clip. Please try again."); }
  };

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = shots.findIndex(s => s.id === active.id);
    const newIndex = shots.findIndex(s => s.id === over.id);
    const newShots = arrayMove([...shots], oldIndex, newIndex);
    try {
      const res = await api.put(`/sessions/${session.id}/storyboard`, { shots: newShots });
      onUpdate(res.data);
      setHasReordered(true);
      toast.success("Shots reordered");
    } catch { toast.error("Could not reorder shots. Please try again."); }
  };

  const handleSaveEdit = async (shotId) => {
    const newShots = shots.map(s => {
      if (s.id !== shotId) return s;
      const updated = { ...s };
      if (s.room_name !== undefined) { updated.room_name = editRoom; updated.voiceover_segment = editText; }
      else { updated.room_label = editRoom; updated.voiceover_text = editText; }
      return updated;
    });
    try {
      const res = await api.put(`/sessions/${session.id}/storyboard`, { shots: newShots });
      onUpdate(res.data);
      setEditingShot(null);
      setHasReordered(true);
      toast.success("Shot updated");
    } catch { toast.error("Could not save changes. Please try again."); }
  };

  const handlePhotoChange = async (shotId, newImageIndex) => {
    const shot = shots.find(s => s.id === shotId);
    if (!shot) return;
    const hasCandidates = (shot.candidates || []).length > 0;
    const newShots = shots.map(s => {
      if (s.id !== shotId) return s;
      const updated = { ...s };
      if (s.related_image_index !== undefined) updated.related_image_index = newImageIndex;
      else updated.image_index = newImageIndex;
      return updated;
    });
    try {
      const res = await api.put(`/sessions/${session.id}/storyboard`, { shots: newShots });
      onUpdate(res.data);
      setPhotoPickerShot(null);
      setPhotoChangedShots(prev => ({ ...prev, [shotId]: true }));
      setHasReordered(true);
      toast.success("Photo updated");
    } catch { toast.error("Could not update photo. Please try again."); }
  };

  const handleUploadNewPhoto = async (file) => {
    setUploadingPhoto(true);
    try {
      const { s3_url } = await presignedUpload(file);
      await api.post(`/sessions/${session.id}/confirm-photo-upload`, { s3_url });
      const sessionRes = await api.get(`/sessions/${session.id}`);
      onUpdate(sessionRes.data);
      const newIndex = sessionRes.data.property_image_urls.length - 1;
      toast.success("Photo uploaded");
      setUploadingPhoto(false);
      // Auto-assign the new photo to the shot
      if (photoPickerShot) handlePhotoChange(photoPickerShot, newIndex);
    } catch {
      toast.error("Upload failed. Please try again.");
      setUploadingPhoto(false);
    }
  };

  const handleRemoveShot = async (shotId) => {
    const newShots = shots.filter(s => s.id !== shotId);
    try {
      const res = await api.put(`/sessions/${session.id}/storyboard`, { shots: newShots });
      onUpdate(res.data);
      setPhotoPickerShot(null);
      toast.success("Shot removed");
    } catch { toast.error("Could not remove shot. Please try again."); }
  };

  const handleSaveAvatarScript = async (segment) => {
    const key = segment === 'intro' ? 'avatar_intro' : 'avatar_cta';
    try {
      const res = await api.put(`/sessions/${session.id}/storyboard`, { [key]: { script: editAvatarScript } });
      onUpdate(res.data);
      setEditingAvatar(null);
      toast.success(`${segment === 'intro' ? 'Intro' : 'CTA'} script updated`);
    } catch { toast.error("Could not save changes. Please try again."); }
  };


  const handleRegenerateScript = async () => {
    setRegeneratingScript(true);
    setChatMessages(prev => [...prev, { role: "user", text: "Regenerate the script to match the current shot order and edits" }]);
    try {
      await api.post(`/sessions/${session.id}/chat`, {
        message: "The shots have been reordered and/or photos have been changed. Regenerate the voiceover script so it flows naturally. IMPORTANT: Look at each attached photo carefully — some photos may have changed since the original script was written. Update room names and descriptions to match what you actually see in each photo. Do NOT preserve old room names if the photo shows a different room."
      });
      const poll = setInterval(async () => {
        try {
          const res = await api.get(`/sessions/${session.id}/chat-status`);
          const { status, summary, error, session: updatedSession } = res.data;
          if (status === 'done') {
            clearInterval(poll);
            setRegeneratingScript(false);
            onUpdate(updatedSession);
            setChatMessages(prev => [...prev, { role: "ai", text: summary }]);
            setHasReordered(false);
            toast.success("Script regenerated");
          } else if (status === 'error') {
            clearInterval(poll);
            setRegeneratingScript(false);
            setChatMessages(prev => [...prev, { role: "ai", text: `I couldn't update the script: ${error}` }]);
            toast.error("Script update didn't go through. Please try again.");
          }
        } catch {}
      }, 3000);
    } catch (err) {
      setChatMessages(prev => [...prev, { role: "ai", text: "I couldn't update the script right now. Please try again." }]);
      toast.error("Script update didn't go through. Please try again.");
      setRegeneratingScript(false);
    }
  };

  const handleApproveRender = async () => {
    setRendering(true);
    try {
      const res = await api.post(`/sessions/${session.id}/approve-render`);
      onUpdate(res.data);
      setShowRenderDialog(false);
      toast.success("Your video is now being created!");
    } catch (err) { toast.error(err.response?.data?.detail || "Something went wrong starting the render. Please try again."); }
    setRendering(false);
  };

  // GENERATING STATE
  if (session.status === "Generating") {
    const progressMsg = session.generation_progress || "Getting your storyboard ready...";
    const skeletonCount = Math.max(5, Math.min(images.length, 15));
    return (
      <div className="flex h-full" data-testid="generating-view">
        <div className="w-80 border-r border-[#E2E4E7] bg-white flex flex-col shrink-0">
          <div className="p-4 border-b border-[#E2E4E7]">
            <h3 className="font-semibold text-[#020B23] text-sm" style={{ fontFamily: 'Sora, sans-serif' }}>AI Script Editor</h3>
            <p className="text-xs text-[#94969C] mt-0.5">Available once storyboard is ready</p>
          </div>
          <div className="flex-1 flex items-center justify-center p-6">
            <div className="text-center space-y-4">
              <div className="w-14 h-14 rounded-2xl bg-[#4D11F6]/10 flex items-center justify-center mx-auto">
                <Sparkles size={24} className="text-[#4D11F6] animate-pulse" />
              </div>
              <p className="text-sm font-medium text-[#020B23]">Generating Storyboard</p>
              <p className="text-xs text-[#94969C] mt-1 max-w-[200px]">{progressMsg}</p>
              <div className="flex justify-center gap-1">
                {[0, 150, 300].map(d => <span key={d} className="w-2 h-2 rounded-full bg-[#4D11F6] animate-bounce" style={{ animationDelay: `${d}ms` }} />)}
              </div>
            </div>
          </div>
          <div className="p-3 border-t border-[#E2E4E7]">
            <div className="flex gap-2">
              <Input placeholder="Chat available after generation..." disabled className="h-9 bg-[#F3F3F9] border-0 text-sm opacity-50" />
              <Button size="icon" disabled className="h-9 w-9 bg-[#E2E4E7] text-[#94969C] shrink-0"><Send size={14} /></Button>
            </div>
          </div>
        </div>
        <div className="flex-1 flex flex-col overflow-hidden">
          <div className="px-6 py-3 bg-white border-b border-[#E2E4E7] flex items-center gap-4">
            <Skeleton className="h-5 w-20" /><Skeleton className="h-2 w-48" />
          </div>
          <ScrollArea className="flex-1 p-6">
            <div className="space-y-4 max-w-4xl">
              <div className="bg-[#4D11F6]/5 rounded-xl border border-[#CACDFE]/50 p-4 animate-pulse">
                <div className="flex items-center gap-3 mb-3"><Skeleton className="w-8 h-8 rounded-full" /><Skeleton className="h-4 w-24" /></div>
                <Skeleton className="h-3 w-3/4 ml-11" />
              </div>
              {Array.from({ length: skeletonCount }).map((_, i) => (
                <div key={i} className="bg-white rounded-xl border border-[#E2E4E7] overflow-hidden animate-pulse" style={{ animationDelay: `${i * 80}ms` }}>
                  <div className="flex">
                    <div className="w-40 shrink-0 bg-[#F3F3F9] relative min-h-[120px]">
                      {images[i] ? <img src={getThumbUrl(images[i])} alt="" className="w-full h-full object-cover opacity-40" loading="lazy" /> : <div className="w-full h-full flex items-center justify-center"><ImageIcon size={24} className="text-[#D8D9DD]" /></div>}
                      <span className="absolute top-2 left-2 text-xs bg-black/40 text-white px-2 py-0.5 rounded-full">#{i + 1}</span>
                    </div>
                    <div className="flex-1 p-4 space-y-3">
                      <Skeleton className="h-4 w-32" /><Skeleton className="h-3 w-full" /><Skeleton className="h-3 w-2/3" />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </ScrollArea>
        </div>
      </div>
    );
  }

  // STORYBOARD + CLIP QA VIEW
  return (
    <div className="flex h-full" data-testid="storyboard-view">
      {/* Chat Panel */}
      <div className="w-80 border-r border-[#E2E4E7] bg-white flex flex-col shrink-0">
        <div className="p-4 border-b border-[#E2E4E7]">
          <h3 className="font-semibold text-[#020B23] text-sm" style={{ fontFamily: 'Sora, sans-serif' }}>AI Script Editor</h3>
          <p className="text-xs text-[#94969C] mt-0.5">Edit your storyboard with natural language</p>
        </div>
        <ScrollArea className="flex-1 p-4">
          <div className="space-y-3">
            {chatMessages.length === 0 && (
              <div className="space-y-2">
                <p className="text-xs text-[#94969C] mb-3">Try these edits:</p>
                {["Make the intro more luxurious", "Swap the kitchen and living room shots", "Add more emotional tone to the master bedroom"].map(s => (
                  <button key={s} onClick={() => setChatInput(s)}
                    className="w-full text-left px-3 py-2 rounded-lg text-xs bg-[#F3F3F9] text-[#676970] hover:bg-[#4D11F6]/5 hover:text-[#4D11F6] transition-colors">{s}</button>
                ))}
              </div>
            )}
            {chatMessages.map((msg, i) => (
              <div key={i} className={msg.role === "user" ? "flex justify-end" : "flex justify-start"}>
                <div className={`max-w-[85%] px-3 py-2 text-sm rounded-xl break-words whitespace-pre-wrap ${msg.role === "user" ? "bg-[#4D11F6] text-white" : "bg-[#F3F3F9] text-[#1A2238]"}`}>{msg.text}</div>
              </div>
            ))}
            {chatLoading && (
              <div className="flex justify-start">
                <div className="bg-[#F3F3F9] rounded-xl px-4 py-2 flex items-center gap-2 text-sm text-[#94969C]"><Loader2 size={14} className="animate-spin" /> Editing storyboard...</div>
              </div>
            )}
            <div ref={chatEndRef} />
          </div>
        </ScrollArea>
        <div className="p-3 border-t border-[#E2E4E7]">
          <div className="flex gap-2 items-end">
            <Textarea value={chatInput} onChange={(e) => setChatInput(e.target.value)}
              placeholder="Edit storyboard..." data-testid="chat-input"
              className="bg-[#F3F3F9] border-0 text-sm min-h-[36px] max-h-[120px] resize-none overflow-y-auto py-2"
              rows={1}
              onInput={(e) => { e.target.style.height = 'auto'; e.target.style.height = Math.min(e.target.scrollHeight, 120) + 'px'; }}
              onKeyDown={(e) => e.key === "Enter" && !e.shiftKey && (e.preventDefault(), handleChat())} />
            <Button size="icon" onClick={handleChat} disabled={chatLoading || !chatInput.trim()}
              data-testid="chat-send-btn" className="h-9 w-9 bg-[#4D11F6] hover:bg-[#3A0BC0] text-white shrink-0"><Send size={14} /></Button>
          </div>
        </div>
      </div>

      {/* Shot Cards */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Action Bar */}
        <div className="px-6 py-3 bg-white border-b border-[#E2E4E7] flex items-center justify-between">
          <div className="flex items-center gap-4">
            <span className="text-sm font-medium text-[#020B23]">{shots.length} Shots</span>
            <div className="flex items-center gap-2 min-w-[200px]">
              <Progress value={progressPct} className="h-2 flex-1" />
              <span className="text-xs text-[#676970] whitespace-nowrap">{selectedCount}/{shots.length} selected</span>
            </div>
          </div>
          <div className="flex gap-2">
            {hasReordered && (
              <Button size="sm" onClick={handleRegenerateScript} disabled={regeneratingScript}
                data-testid="regenerate-script-btn"
                className="text-sm bg-amber-500 hover:bg-amber-600 text-white animate-pulse">
                {regeneratingScript ? <Loader2 size={13} className="mr-1.5 animate-spin" /> : <RefreshCw size={13} className="mr-1.5" />}
                Regenerate Script
              </Button>
            )}
            <Button size="sm" onClick={handleGenerateAll} disabled={generatingAll}
              data-testid="generate-all-clips-btn" className="bg-[#4D11F6] hover:bg-[#3A0BC0] text-white text-sm">
              {generatingAll ? <><Loader2 size={13} className="mr-1.5 animate-spin" /> Creating All Clips...</> : <><Zap size={13} className="mr-1.5" /> Create All Clips</>}
            </Button>
            {!hideRenderButton && (
              <TooltipProvider><Tooltip><TooltipTrigger asChild><span>
                <Button size="sm" onClick={() => setShowRenderDialog(true)} disabled={!allReady}
                  data-testid="approve-render-btn"
                  className={`text-sm ${allReady ? "bg-[#4D11F6] hover:bg-[#3A0BC0] text-white" : "bg-[#E2E4E7] text-[#94969C] cursor-not-allowed"}`}>
                  <Film size={13} className="mr-1.5" /> Approve & Render
                </Button>
              </span></TooltipTrigger>
              {!allReady && <TooltipContent><p className="text-xs">Please select a clip for each shot before rendering</p></TooltipContent>}
              </Tooltip></TooltipProvider>
            )}
          </div>
        </div>

        {/* Shots List with DnD */}
        <ScrollArea className="flex-1 p-6">
          <div className="space-y-4 max-w-4xl relative">
            {/* Skeleton overlay while AI is editing */}
            {(chatLoading || regeneratingScript) && (
              <div className="absolute inset-0 z-40 bg-white/70 backdrop-blur-[2px] rounded-xl flex flex-col items-center justify-center gap-4 min-h-[300px]" data-testid="ai-editing-overlay">
                <div className="w-14 h-14 rounded-2xl bg-[#4D11F6]/10 flex items-center justify-center">
                  <Sparkles size={24} className="text-[#4D11F6] animate-pulse" />
                </div>
                <p className="text-sm font-medium text-[#020B23]" style={{ fontFamily: 'Sora, sans-serif' }}>AI is editing the storyboard...</p>
                <p className="text-xs text-[#94969C] max-w-[280px] text-center">This may take 15-30 seconds. The script will update automatically when ready.</p>
                <div className="flex gap-1 mt-1">
                  {[0, 150, 300].map(d => <span key={d} className="w-2 h-2 rounded-full bg-[#4D11F6] animate-bounce" style={{ animationDelay: `${d}ms` }} />)}
                </div>
              </div>
            )}
            {/* Avatar Intro */}
            {storyboard.avatar_intro?.script && (
              <div className="bg-gradient-to-r from-[#4D11F6]/5 to-[#6366f1]/5 rounded-xl border border-[#CACDFE] p-4" data-testid="avatar-intro-card">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-8 h-8 rounded-full bg-[#4D11F6]/10 flex items-center justify-center"><User size={14} className="text-[#4D11F6]" /></div>
                  <div><h4 className="font-semibold text-[#020B23] text-sm" style={{ fontFamily: 'Sora, sans-serif' }}>Avatar Intro</h4>
                  <span className="text-xs text-[#94969C]">{storyboard.avatar_intro.duration_estimate || '~8s'}</span></div>
                  <Badge className="ml-auto text-xs bg-[#E6EBFF] text-[#4D11F6] border-[#CACDFE]">Intro</Badge>
                  {editingAvatar !== 'intro' && (
                    <button onClick={() => { setEditingAvatar('intro'); setEditAvatarScript(storyboard.avatar_intro.script); }}
                      data-testid="edit-avatar-intro" className="p-1.5 text-[#94969C] hover:text-[#4D11F6] hover:bg-[#4D11F6]/5 rounded transition-colors" title="Edit intro script">
                      <Pencil size={13} />
                    </button>
                  )}
                </div>
                {editingAvatar === 'intro' ? (
                  <div className="pl-11 space-y-2" data-testid="edit-form-avatar-intro">
                    <Textarea value={editAvatarScript} onChange={(e) => setEditAvatarScript(e.target.value)}
                      className="text-sm border-[#E2E4E7] min-h-[60px]" placeholder="Intro script" />
                    <div className="flex gap-2">
                      <Button size="sm" onClick={() => handleSaveAvatarScript('intro')}
                        data-testid="save-avatar-intro" className="text-xs bg-[#4D11F6] hover:bg-[#3A0BC0] text-white h-7">
                        <Save size={11} className="mr-1" /> Save
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => setEditingAvatar(null)} className="text-xs border-[#E2E4E7] h-7">Cancel</Button>
                    </div>
                  </div>
                ) : (
                  <p className="text-sm text-[#676970] pl-11">{storyboard.avatar_intro.script}</p>
                )}
              </div>
            )}

            {/* Draggable Shot Cards */}
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
              <SortableContext items={shots.map(s => s.id)} strategy={verticalListSortingStrategy}>
                {shots.map((shot, idx) => {
                  const candidates = shot.candidates || [];
                  const hasSelected = !!shot.selected_candidate_id;
                  const imgIdx = getShotField(shot, 'imageIdx');
                  const imgEntry = images[imgIdx % Math.max(1, images.length)];
                  const thumbUrl = getThumbUrl(imgEntry);
                  const fullUrl = getImageUrl(imgEntry);
                  const isEditing = editingShot === shot.id;

                  return (
                    <SortableShotCard key={shot.id} shot={shot} idx={idx}>
                      {({ listeners }) => (
                        <div data-testid={`shot-card-${shot.id}`}
                          className="bg-white rounded-xl border border-[#E2E4E7] overflow-hidden hover:shadow-md transition-shadow">
                          <div className="flex">
                            {/* Drag Handle + Thumbnail */}
                            <div className="flex">
                              <div {...listeners} className="w-8 flex items-center justify-center bg-[#F3F3F9] cursor-grab active:cursor-grabbing hover:bg-[#E2E4E7] transition-colors"
                                data-testid={`drag-handle-${shot.id}`}>
                                <GripVertical size={14} className="text-[#94969C]" />
                              </div>
                              <div className="w-36 shrink-0 bg-[#F3F3F9] relative group cursor-pointer"
                                onClick={() => thumbUrl ? setLightboxImage(fullUrl) : null}>
                                {thumbUrl ? (
                                  <img src={thumbUrl} alt={getShotField(shot, 'room')} className="w-full h-full object-cover min-h-[120px]" loading="lazy" />
                                ) : (
                                  <div className="w-full h-full flex items-center justify-center min-h-[120px]"><ImageIcon size={24} className="text-[#D8D9DD]" /></div>
                                )}
                                <span className="absolute top-2 left-2 text-xs bg-black/60 text-white px-2 py-0.5 rounded-full font-medium">#{idx + 1}</span>
                                {/* Overlay actions */}
                                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors flex items-center justify-center gap-2 opacity-0 group-hover:opacity-100">
                                  <button onClick={(e) => { e.stopPropagation(); setLightboxImage(fullUrl); }}
                                    className="w-8 h-8 rounded-full bg-white/90 flex items-center justify-center" title="View full image">
                                    <Maximize2 size={12} className="text-[#020B23]" />
                                  </button>
                                  <button onClick={(e) => { e.stopPropagation(); setPhotoPickerShot(shot.id); }}
                                    className="w-8 h-8 rounded-full bg-white/90 flex items-center justify-center" title="Change photo">
                                    <ArrowLeftRight size={12} className="text-[#020B23]" />
                                  </button>
                                </div>
                              </div>
                            </div>

                            {/* Content */}
                            <div className="flex-1 p-4">
                              {isEditing ? (
                                /* Editing mode */
                                <div className="space-y-3" data-testid={`edit-form-${shot.id}`}>
                                  <Input value={editRoom} onChange={(e) => setEditRoom(e.target.value)}
                                    className="h-8 text-sm font-semibold border-[#E2E4E7]" placeholder="Room name" />
                                  <Textarea value={editText} onChange={(e) => setEditText(e.target.value)}
                                    className="text-sm border-[#E2E4E7] min-h-[60px]" placeholder="Voiceover text" />
                                  <div className="flex gap-2">
                                    <Button size="sm" onClick={() => handleSaveEdit(shot.id)}
                                      data-testid={`save-edit-${shot.id}`}
                                      className="text-xs bg-[#4D11F6] hover:bg-[#3A0BC0] text-white h-7">
                                      <Save size={11} className="mr-1" /> Save
                                    </Button>
                                    <Button size="sm" variant="outline" onClick={() => setEditingShot(null)}
                                      className="text-xs border-[#E2E4E7] h-7">Cancel</Button>
                                  </div>
                                </div>
                              ) : (
                                /* Display mode */
                                <>
                                  <div className="flex items-start justify-between mb-2">
                                    <div>
                                      <h4 className="font-semibold text-[#020B23] text-sm" style={{ fontFamily: 'Sora, sans-serif' }}>
                                        {getShotField(shot, 'room')}
                                      </h4>
                                      <div className="flex items-center gap-3 mt-1 flex-wrap">
                                        <span className="text-xs text-[#94969C] flex items-center gap-1"><Clock size={11} /> {shot.duration}s</span>
                                        {shot.motion_type && <span className="text-xs text-[#94969C] flex items-center gap-1"><Video size={11} /> {shot.motion_type}</span>}
                                        <Badge className={`text-xs ${hasSelected ? "bg-emerald-50 text-emerald-700 border-emerald-100" : shot.status === 'Generating' ? "bg-amber-50 text-amber-700 border-amber-100" : shot.status === 'Failed' ? "bg-red-50 text-red-600 border-red-100" : candidates.length > 0 ? "bg-blue-50 text-blue-700 border-blue-100" : "bg-[#F3F3F9] text-[#94969C] border-[#E2E4E7]"}`}>
                                          {shot.status === 'Generating' && <Loader2 size={10} className="mr-1 animate-spin" />}
                                          {hasSelected ? "Selected" : shot.status === 'Generating' ? "Creating..." : shot.status === 'Failed' ? "Needs Retry" : candidates.length > 0 ? "Ready" : "Pending"}
                                        </Badge>
                                      </div>
                                    </div>
                                    <div className="flex items-center gap-1">
                                      <button onClick={() => { setEditingShot(shot.id); setEditText(getShotField(shot, 'voiceover')); setEditRoom(getShotField(shot, 'room')); }}
                                        data-testid={`edit-shot-${shot.id}`}
                                        className="p-1.5 text-[#94969C] hover:text-[#4D11F6] hover:bg-[#4D11F6]/5 rounded transition-colors" title="Edit">
                                        <Pencil size={13} />
                                      </button>
                                      <button onClick={() => setExpandedShot(expandedShot === shot.id ? null : shot.id)}
                                        className="p-1.5 text-[#94969C] hover:text-[#4D11F6] hover:bg-[#4D11F6]/5 rounded transition-colors" title="Details">
                                        <Eye size={13} />
                                      </button>
                                    </div>
                                  </div>
                                  <p className="text-sm text-[#676970] mb-3 leading-relaxed">{getShotField(shot, 'voiceover')}</p>
                                </>
                              )}

                              {/* Expanded: video_gen_prompt */}
                              {expandedShot === shot.id && shot.video_gen_prompt && !isEditing && (
                                <div className="mb-3 p-3 bg-[#F3F3F9] rounded-lg">
                                  <p className="text-xs font-medium text-[#1A2238] mb-1">Video Instructions</p>
                                  <p className="text-xs text-[#676970] leading-relaxed">{shot.video_gen_prompt}</p>
                                </div>
                              )}

                              {/* Candidates / Video Preview */}
                              {!isEditing && (
                                <div className="mb-3">
                                  {shot.status === 'Generating' && !candidates.some(c => c.status === 'Ready') && (
                                    <div className="flex items-center gap-3 p-3 bg-[#F3F3F9] rounded-lg animate-pulse" data-testid={`generating-${shot.id}`}>
                                      <Loader2 size={16} className="text-[#4D11F6] animate-spin" />
                                      <span className="text-xs text-[#676970]">Creating your video clip — this may take a moment...</span>
                                    </div>
                                  )}
                                  {candidates.length > 0 && (
                                    <div className="flex gap-2 flex-wrap">
                                      {candidates.map(cand => (
                                        <div key={cand.id} className="relative group">
                                          {cand.status === 'Generating' ? (
                                            <div className="w-32 h-20 rounded-lg border-2 border-[#E2E4E7] bg-[#F3F3F9] flex flex-col items-center justify-center gap-1">
                                              <Loader2 size={14} className="text-[#4D11F6] animate-spin" />
                                              <span className="text-[10px] text-[#94969C]">Creating...</span>
                                            </div>
                                          ) : cand.status === 'Failed' ? (
                                            <div className="w-32 h-20 rounded-lg border-2 border-red-200 bg-red-50 flex flex-col items-center justify-center gap-1 cursor-pointer"
                                              title={cand.error || 'Something went wrong'}>
                                              <AlertCircle size={14} className="text-red-500" />
                                              <span className="text-[10px] text-red-500">Try again</span>
                                            </div>
                                          ) : (
                                            <button onClick={() => {
                                              if (cand.video_url) setVideoPlayer({ url: cand.video_url, title: `${getShotField(shot, 'room')} - Clip ${candidates.indexOf(cand) + 1}` });
                                            }}
                                              data-testid={`candidate-${cand.id}`}
                                              className={`w-32 h-20 rounded-lg border-2 overflow-hidden transition-all relative ${shot.selected_candidate_id === cand.id ? "border-[#4D11F6] shadow-[0_0_0_2px_rgba(77,17,246,0.2)]" : "border-[#E2E4E7] hover:border-[#4D11F6]/40"}`}>
                                              {cand.thumbnail_url ? <img src={cand.thumbnail_url} alt="" className="w-full h-full object-cover" /> :
                                                <div className="w-full h-full bg-[#F3F3F9]" />}
                                              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors flex items-center justify-center opacity-0 group-hover:opacity-100">
                                                <Play size={20} className="text-white drop-shadow" />
                                              </div>
                                              {shot.selected_candidate_id === cand.id && (
                                                <div className="absolute top-1 right-1 w-4 h-4 rounded-full bg-[#4D11F6] flex items-center justify-center">
                                                  <Check size={10} className="text-white" />
                                                </div>
                                              )}
                                            </button>
                                          )}
                                          {cand.status === 'Ready' && (
                                            <button onClick={() => handleSelectCandidate(shot.id, cand.id)}
                                              className={`mt-1 w-full text-center text-[10px] py-0.5 rounded ${shot.selected_candidate_id === cand.id ? 'text-[#4D11F6] font-medium' : 'text-[#94969C] hover:text-[#4D11F6]'}`}>
                                              {shot.selected_candidate_id === cand.id ? 'Selected' : 'Select'}
                                            </button>
                                          )}
                                        </div>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              )}

                              {/* Photo changed banner */}
                              {photoChangedShots[shot.id] && !isEditing && (candidates.length > 0 || hasReordered) && (
                                <div className="flex items-center gap-2 p-2 bg-amber-50 border border-amber-200 rounded-lg mb-1">
                                  <AlertCircle size={14} className="text-amber-600 shrink-0" />
                                  <span className="text-xs text-amber-800 flex-1">
                                    {hasReordered
                                      ? "Photo changed — use Regenerate Script above to update."
                                      : "Clip was generated from a different photo."}
                                  </span>
                                  {candidates.length > 0 && (
                                    <Button size="sm" onClick={() => {
                                      handleGenerateShot(shot.id);
                                      setPhotoChangedShots(prev => { const next = {...prev}; delete next[shot.id]; return next; });
                                    }}
                                      className="text-xs h-7 px-3 bg-amber-500 hover:bg-amber-600 text-white shrink-0">
                                      <RefreshCw size={11} className="mr-1" /> Regenerate Clip
                                    </Button>
                                  )}
                                </div>
                              )}

                              {/* Actions */}
                              {!isEditing && (
                                <div className="flex flex-col gap-2">
                                  {promptingShot === shot.id ? (
                                    <div className="flex flex-col gap-2 w-full" data-testid={`clip-prompt-form-${shot.id}`}>
                                      <Input
                                        value={clipPromptText}
                                        onChange={e => setClipPromptText(e.target.value)}
                                        placeholder="e.g. fast tracking shot, slow dolly zoom..."
                                        className="text-xs h-8"
                                        data-testid={`clip-prompt-input-${shot.id}`}
                                        autoFocus
                                        onKeyDown={e => { if (e.key === 'Enter') { handleGenerateShot(shot.id, clipPromptText || null); setPromptingShot(null); setClipPromptText(""); } }}
                                      />
                                      <div className="flex gap-2">
                                        <Button size="sm"
                                          onClick={() => { handleGenerateShot(shot.id, clipPromptText || null); setPromptingShot(null); setClipPromptText(""); }}
                                          disabled={generatingShot === shot.id}
                                          data-testid={`clip-prompt-submit-${shot.id}`}
                                          className="text-xs h-7 px-3 bg-[#4D11F6] hover:bg-[#3A0BC0] text-white">
                                          <Sparkles size={12} className="mr-1.5" /> Generate
                                        </Button>
                                        <Button size="sm" variant="outline"
                                          onClick={() => { setPromptingShot(null); setClipPromptText(""); }}
                                          data-testid={`clip-prompt-cancel-${shot.id}`}
                                          className="text-xs h-7 px-3 border-[#E2E4E7]">
                                          Cancel
                                        </Button>
                                      </div>
                                    </div>
                                  ) : (
                                    <div className="flex gap-2 items-center">
                                      <Button size="sm"
                                        onClick={() => { setPromptingShot(shot.id); setClipPromptText(""); }}
                                        disabled={generatingShot === shot.id}
                                        data-testid={`generate-shot-${shot.id}`}
                                        className={`text-xs h-8 px-3 ${candidates.length === 0 ? "bg-[#4D11F6] hover:bg-[#3A0BC0] text-white" : "bg-[#E6EBFF] text-[#4D11F6] hover:bg-[#D6DBFF] border-0"}`}>
                                        {generatingShot === shot.id ? <><Loader2 size={12} className="mr-1.5 animate-spin" /> Creating...</> :
                                         candidates.length === 0 ? <><Sparkles size={12} className="mr-1.5" /> Create Clip</> :
                                         <><RefreshCw size={12} className="mr-1.5" /> + New Version</>}
                                      </Button>
                                      {candidates.length > 0 && <span className="text-xs text-[#94969C]">{candidates.length} clip{candidates.length !== 1 ? 's' : ''}</span>}
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      )}
                    </SortableShotCard>
                  );
                })}
              </SortableContext>
            </DndContext>

            {/* Avatar CTA */}
            {storyboard.avatar_cta?.script && (
              <div className="bg-gradient-to-r from-emerald-50/50 to-teal-50/50 rounded-xl border border-emerald-200 p-4" data-testid="avatar-cta-card">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center"><User size={14} className="text-emerald-600" /></div>
                  <div><h4 className="font-semibold text-[#020B23] text-sm" style={{ fontFamily: 'Sora, sans-serif' }}>Avatar CTA</h4>
                  <span className="text-xs text-[#94969C]">{storyboard.avatar_cta.duration_estimate || '~8s'}</span></div>
                  <Badge className="ml-auto text-xs bg-emerald-50 text-emerald-700 border-emerald-100">Closing</Badge>
                  {editingAvatar !== 'cta' && (
                    <button onClick={() => { setEditingAvatar('cta'); setEditAvatarScript(storyboard.avatar_cta.script); }}
                      data-testid="edit-avatar-cta" className="p-1.5 text-[#94969C] hover:text-emerald-600 hover:bg-emerald-50 rounded transition-colors" title="Edit CTA script">
                      <Pencil size={13} />
                    </button>
                  )}
                </div>
                {editingAvatar === 'cta' ? (
                  <div className="pl-11 space-y-2" data-testid="edit-form-avatar-cta">
                    <Textarea value={editAvatarScript} onChange={(e) => setEditAvatarScript(e.target.value)}
                      className="text-sm border-[#E2E4E7] min-h-[60px]" placeholder="CTA script" />
                    <div className="flex gap-2">
                      <Button size="sm" onClick={() => handleSaveAvatarScript('cta')}
                        data-testid="save-avatar-cta" className="text-xs bg-emerald-600 hover:bg-emerald-700 text-white h-7">
                        <Save size={11} className="mr-1" /> Save
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => setEditingAvatar(null)} className="text-xs border-[#E2E4E7] h-7">Cancel</Button>
                    </div>
                  </div>
                ) : (
                  <p className="text-sm text-[#676970] pl-11">{storyboard.avatar_cta.script}</p>
                )}
              </div>
            )}

            {/* Voiceover Summary */}
            {storyboard.voiceover_script?.full_body_text && (
              <div className="bg-[#F3F3F9] rounded-xl p-4 mt-2" data-testid="voiceover-summary">
                <h4 className="font-semibold text-[#020B23] text-xs mb-2 uppercase tracking-wide">Full Voiceover Script</h4>
                <p className="text-sm text-[#676970] leading-relaxed">{storyboard.voiceover_script.full_body_text}</p>
              </div>
            )}
          </div>
        </ScrollArea>
      </div>

      {/* Lightbox */}
      <Dialog open={!!lightboxImage} onOpenChange={() => setLightboxImage(null)}>
        <DialogContent className="sm:max-w-4xl p-0 bg-black border-0 overflow-hidden" data-testid="lightbox-dialog">
          {lightboxImage && (
            <img src={lightboxImage} alt="Full view" className="w-full h-auto max-h-[85vh] object-contain" />
          )}
        </DialogContent>
      </Dialog>

      {/* Video Player */}
      <Dialog open={!!videoPlayer} onOpenChange={() => setVideoPlayer(null)}>
        <DialogContent className="sm:max-w-4xl p-0 bg-black border-0 overflow-hidden" data-testid="video-player-dialog">
          {videoPlayer && (
            <div className="relative">
              <div className="p-3 bg-black/80 border-b border-white/10">
                <h3 className="text-white text-sm font-medium" style={{ fontFamily: 'Sora, sans-serif' }}>{videoPlayer.title}</h3>
              </div>
              <video
                src={videoPlayer.url}
                controls
                autoPlay
                className="w-full max-h-[75vh]"
                style={{ backgroundColor: '#000' }}
              />
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Photo Picker */}
      <PhotoPickerDialog
        open={!!photoPickerShot}
        onClose={() => setPhotoPickerShot(null)}
        images={images}
        currentImageIndex={photoPickerShot ? getShotField(shots.find(s => s.id === photoPickerShot) || {}, 'imageIdx') : 0}
        onSelectPhoto={(idx) => handlePhotoChange(photoPickerShot, idx)}
        onUploadNewPhoto={handleUploadNewPhoto}
        onRemoveShot={() => handleRemoveShot(photoPickerShot)}
        uploading={uploadingPhoto}
      />

      {/* Render Dialog */}
      <Dialog open={showRenderDialog} onOpenChange={setShowRenderDialog}>
        <DialogContent className="sm:max-w-md bg-white" data-testid="render-confirm-dialog">
          <DialogHeader><DialogTitle style={{ fontFamily: 'Sora, sans-serif' }} className="text-[#020B23]">Approve & Render</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <p className="text-sm text-[#676970]">Ready to create your final walkthrough video.</p>
            <div className="bg-[#F3F3F9] rounded-lg p-4 space-y-2">
              <div className="flex items-center gap-2 text-sm"><CheckCircle2 size={14} className="text-emerald-600" /><span className="text-[#1A2238]">All {shots.length} shots are ready to go</span></div>
            </div>
            <div className="flex items-start gap-2 p-3 rounded-lg bg-amber-50 border border-amber-100">
              <AlertCircle size={16} className="text-amber-600 mt-0.5 shrink-0" />
              <p className="text-xs text-amber-800">This will use credits from your account to produce the final video.</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRenderDialog(false)} className="border-[#E2E4E7]">Cancel</Button>
            <Button onClick={handleApproveRender} disabled={rendering} data-testid="confirm-render-btn"
              className="bg-[#4D11F6] hover:bg-[#3A0BC0] text-white">
              {rendering ? <><Loader2 size={14} className="mr-2 animate-spin" /> Starting...</> : <><Film size={14} className="mr-2" /> Approve & Render</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
